import type Stripe from 'stripe';

export const concatUniqueArray = <T>(...args: T[][]): T[] => {
  return [...new Set(args.flat())];
};

const getBytesFromKilobytes = (kilobytes: bigint): bigint => {
  return kilobytes * 1024n;
};

export const getBytesFromMegabytes = (megabytes: bigint): bigint => {
  return getBytesFromKilobytes(megabytes) * 1024n;
};

export const getBytesFromGigabytes = (gigabytes: bigint): bigint => {
  return getBytesFromMegabytes(gigabytes) * 1024n;
};
export const getBytesFromTerabytes = (terabytes: bigint): bigint => {
  return getBytesFromGigabytes(terabytes) * 1024n;
};

type BillingScopes = typeof billingScopes;
type CoreScopes = BillingScopes['core'][keyof BillingScopes['core']];
type AssetScopes = BillingScopes['asset'][keyof BillingScopes['asset']];
type AssetVersionScopes = BillingScopes['assetVersion'][keyof BillingScopes['assetVersion']];
type GalleryScopes = BillingScopes['gallery'][keyof BillingScopes['gallery']];
type GalleryPresetsScopes = BillingScopes['galleryPresets'][keyof BillingScopes['galleryPresets']];
type TenantScopes = BillingScopes['tenant'][keyof BillingScopes['tenant']];
type WatermarkScopes = BillingScopes['watermark'][keyof BillingScopes['watermark']];
type GroupsScopes = BillingScopes['groups'][keyof BillingScopes['groups']];
type GroupUsersScopes = BillingScopes['groupUsers'][keyof BillingScopes['groupUsers']];
type GroupGalleriesScopes = BillingScopes['groupGalleries'][keyof BillingScopes['groupGalleries']];
type tenantColorScopes = BillingScopes['tenantColors'][keyof BillingScopes['tenantColors']];

export type BillingScope =
  | CoreScopes
  | AssetScopes
  | AssetVersionScopes
  | GalleryScopes
  | GalleryPresetsScopes
  | TenantScopes
  | WatermarkScopes
  | GroupsScopes
  | GroupUsersScopes
  | GroupGalleriesScopes
  | tenantColorScopes;

export const billingScopes = {
  core: {
    storageAddons: 'core:storage-addons',
  },
  asset: {
    favourites: 'asset:favourites',
    comments: 'asset:comments',
    flags: 'asset:flags',
    status: 'asset:status',
    colorLabels: 'asset:color-labels',
    customLabels: 'asset:custom-labels',
    upload: 'asset:upload',
    uploadRaw: 'asset:upload-raw',
    uploadAny: 'asset:upload-any',
    download: 'asset:download',
    downloadOriginal: 'asset:download-original',
    watermarking: 'asset:watermarking',
    fileAttachment: 'asset:file-attachment',
    embedRightClickProtection: 'asset:embed-right-click-protection',
    video: 'asset:video',
    createCdnLink: 'asset:create-cdn-link',
    customVideoPoster: 'asset:custom-video-poster',
    autoplay: 'asset:autoplay',
    privateComments: 'asset:private-comments',
    videoOutputSD: 'asset:video-output-sd',
    videoOutputHD: 'asset:video-output-hd',
    videoOutputFullHD: 'asset:video-output-fullhd',
    videoOutputQHD: 'asset:video-output-qhd',
    videoOutput4K: 'asset:video-output-4k',
  },
  assetVersion: {
    multiple: 'asset-version:multiple',
  },
  gallery: {
    password: 'gallery:password',
    subGallery: 'gallery:sub-gallery',
    embedSection: 'gallery:embed-section',
    embedGallery: 'gallery:embed-gallery',
    clientAdvancedMode: 'gallery:client-advanced-mode',
    clientLayoutMode: 'gallery:client-layout-mode',
    approvalLimit: 'gallery:approval-limit',
    removePicflowBranding: 'gallery:remove-picflow-branding',
    customPresets: 'gallery:custom-presets',
    forceIdentification: 'gallery:force-identification',
    approvedDownloadsOnly: 'gallery:approved-downloads-only',
    downloadFromEmbed: 'gallery:download-from-embed',
    videoInCover: 'gallery:video-in-cover',
    otherSourceUpload: 'gallery:other-source-upload',
    themes: 'gallery:themes',
  },
  galleryPresets: {
    approvedDownloadsOnly: 'gallery-presets:approved-downloads-only',
  },
  tenant: {
    logoUpload: 'tenant:logo-upload',
    customDomain: 'tenant:custom-domain',
    customFonts: 'tenant:custom-fonts',
    customGoogleAnalytics: 'tenant:custom-google-analytics',
    customGoogleTagManager: 'tenant:custom-google-tag-manager',
    zapier: 'tenant:zapier',
    blockPublicLinkAccess: 'tenant:block-public-link-access',
    enforce2fa: 'tenant:enforce-2fa',
    defaultTypography: 'tenant:default-typography',
  },
  tenantColors: {
    create: 'tenant-colors:create',
    update: 'tenant-colors:update',
  },
  watermark: {
    update: 'watermark:update',
    create: 'watermark:create',
  },
  groups: {
    list: 'groups:list',
    create: 'groups:create',
    retrieve: 'groups:retrieve',
    update: 'groups:update',
    delete: 'groups:delete',
  },
  groupUsers: {
    add: 'group-users:add',
    remove: 'group-users:remove',
    list: 'group-users:list',
  },
  groupGalleries: {
    list: 'group-galleries:list',
    attach: 'group-galleries:attach',
    detach: 'group-galleries:detach',
    retrieve: 'group-galleries:retrieve',
    update: 'group-galleries:update',
  },
  groupFolders: {
    list: 'group-folders:list',
    attach: 'group-folders:attach',
    detach: 'group-folders:detach',
    retrieve: 'group-folders:retrieve',
    update: 'group-folders:update',
  },
} as const;

export const freeScopes = [
  billingScopes.asset.favourites,
  billingScopes.asset.comments,
  billingScopes.asset.upload,
  billingScopes.asset.download,
  billingScopes.asset.downloadOriginal,
  billingScopes.asset.flags,
  billingScopes.asset.status,
  billingScopes.gallery.clientAdvancedMode,
  billingScopes.gallery.embedGallery,
];

export const plusScopes = concatUniqueArray(freeScopes, [
  billingScopes.core.storageAddons,
  billingScopes.asset.colorLabels,
  billingScopes.assetVersion.multiple,
  billingScopes.gallery.subGallery,
  billingScopes.gallery.embedSection,
  billingScopes.gallery.removePicflowBranding,
  billingScopes.gallery.password,
  billingScopes.gallery.forceIdentification,
  billingScopes.gallery.downloadFromEmbed,
  billingScopes.asset.embedRightClickProtection,
  billingScopes.asset.video,
  billingScopes.gallery.otherSourceUpload,
  billingScopes.tenant.logoUpload,
  billingScopes.asset.videoOutputSD,
  billingScopes.asset.videoOutputHD,
  billingScopes.asset.videoOutputFullHD,
  billingScopes.asset.uploadAny, // not on the backend yet
]);

export const proLegacy2021Scopes = concatUniqueArray(plusScopes, [
  billingScopes.asset.customLabels,
  billingScopes.asset.uploadRaw,
  billingScopes.asset.watermarking,
  billingScopes.asset.fileAttachment,
  billingScopes.asset.createCdnLink,
  billingScopes.asset.customVideoPoster,
  billingScopes.gallery.clientLayoutMode,
  billingScopes.gallery.approvalLimit,
  billingScopes.gallery.customPresets,
  billingScopes.gallery.approvedDownloadsOnly,
  billingScopes.gallery.videoInCover,
  billingScopes.galleryPresets.approvedDownloadsOnly,
  billingScopes.tenant.customDomain,
  billingScopes.tenant.customGoogleAnalytics,
  billingScopes.watermark.create,
  billingScopes.watermark.update,
  billingScopes.tenantColors.create,
  billingScopes.tenantColors.update,
  billingScopes.tenant.defaultTypography,
  billingScopes.tenant.customFonts,
  billingScopes.tenant.zapier,
  billingScopes.asset.autoplay,
  billingScopes.asset.videoOutputQHD,
  billingScopes.asset.videoOutput4K,
  billingScopes.gallery.themes,
]);

export const proScopes = concatUniqueArray(proLegacy2021Scopes, []);

export const bizScopes = concatUniqueArray(proScopes, [
  billingScopes.tenant.customGoogleTagManager,
  billingScopes.asset.privateComments,
]);

export const enterpriseScopes = concatUniqueArray(bizScopes, [
  billingScopes.groups.list,
  billingScopes.groups.create,
  billingScopes.groups.retrieve,
  billingScopes.groups.update,
  billingScopes.groups.delete,
  billingScopes.groupUsers.add,
  billingScopes.groupUsers.remove,
  billingScopes.groupUsers.list,
  billingScopes.groupGalleries.attach,
  billingScopes.groupGalleries.detach,
  billingScopes.groupGalleries.list,
  billingScopes.groupGalleries.update,
  billingScopes.tenant.blockPublicLinkAccess,
  billingScopes.tenant.enforce2fa,
  billingScopes.groupFolders.attach,
  billingScopes.groupFolders.detach,
  billingScopes.groupFolders.list,
  billingScopes.groupFolders.update,
]);

export const productScopes = {
  free: freeScopes,
  plus: plusScopes,
  pro: proScopes,
  biz: bizScopes,
  enterprise: enterpriseScopes,
};

export const freeStripeProduct = <Stripe.Product>{
  metadata: <Stripe.Metadata>{ included_seat_count: '1', storage_capacity: '2' },
  name: 'Picflow Free',
  id: 'free',
  active: true,
};

export const freeStripeProductPrice = <Stripe.Price & { product: Stripe.Product }>{
  unit_amount: 0,
  recurring: { interval: 'year' },
  product: freeStripeProduct,
  metadata: freeStripeProduct.metadata,
};

export const enterpriseStripeProduct = <Stripe.Product>{
  metadata: <Stripe.Metadata>{
    included_seat_count: '-1',
    storage_capacity: '-1',
  },
  name: 'Picflow Enterprise',
  id: 'enterprise',
  active: true,
};

export const freePlanMaxSeats = 2;
